// File: frontend/src/context/SchoolContext.js

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/axios';
import { AuthContext } from './AuthContext';

const SchoolContext = createContext();

/**
 * Helper function to format relative upload paths into absolute backend URLs
 */
export const getFullImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path.replace('/api/uploads/', '/uploads/');
  }
  const rawBase = process.env.REACT_APP_API_URL || 'https://school.grovvia.site';
  const cleanBase = rawBase.replace(/\/api\/?$/, '').replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${cleanBase}${cleanPath}`;
};

export const SchoolProvider = ({ children }) => {
  const auth = useContext(AuthContext);
  const user = auth?.user;
  const token = auth?.token;

  const [school, setSchool] = useState({
    name: 'MarkazSchool Pro Portal',
    logo: null,
    stamp: null,
    address: 'Campus Address',
    phone: '',
    email: '',
    primary_color: '#2563eb',
    secondary_color: '#0f172a',
    session_year: '2026',
    principal_name: 'Principal',
    principal_signature: null,
    receipt_header: 'Official Fee Payment Receipt',
    result_footer: 'This report card is computer generated and officially valid.',
    license_key: 'SMS-2026-LIC-8894',
    license_expiry: '2027-12-31',
    is_licensed: true,
    website: ''
  });

  const [loading, setLoading] = useState(true);

  /**
   * Fetch school settings & branding config from backend API
   */
  const loadSchoolSettings = useCallback(async () => {
    try {
      setLoading(true);
      const [settingsRes, brandingRes] = await Promise.all([
        api.get('/api/v1/settings').catch(() => ({ data: null })),
        api.get('/api/v1/branding/config').catch(() => ({ data: null }))
      ]);

      let schoolData = {};
      if (settingsRes.data?.success && settingsRes.data.data) {
        schoolData = settingsRes.data.data;
      }

      let brandingData = {};
      if (brandingRes.data?.success && brandingRes.data.data) {
        brandingData = brandingRes.data.data;
      }

      const mergedColors = {
        primary_color: brandingData.colors?.primary || schoolData.primary_color || '#0F172A',
        secondary_color: brandingData.colors?.secondary || schoolData.secondary_color || '#1E293B',
        tertiary_color: brandingData.colors?.tertiary || schoolData.tertiary_color || '#10B981',
        button_color: brandingData.colors?.button || schoolData.button_color || '#2563EB',
        font_family: brandingData.typography?.font_family || 'Inter, sans-serif',
        border_radius: brandingData.typography?.border_radius || '12px'
      };

      setSchool({
        ...schoolData,
        ...mergedColors,
        portal_name: brandingData.portal_name || schoolData.portal_name || 'MarkazSchool Pro Portal',
        logo: getFullImageUrl(schoolData.logo),
        stamp: getFullImageUrl(schoolData.stamp),
        principal_signature: getFullImageUrl(schoolData.principal_signature)
      });
    } catch (error) {
      console.error('Error loading school settings in context:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSchoolSettings();
  }, [loadSchoolSettings, user, token]);

  // Inject dynamic CSS variables into document root element for theme colors & typography
  useEffect(() => {
    if (school.primary_color) {
      document.documentElement.style.setProperty('--primary-color', school.primary_color);
      document.documentElement.style.setProperty('--color-primary', school.primary_color);
    }
    if (school.secondary_color) {
      document.documentElement.style.setProperty('--secondary-color', school.secondary_color);
    }
    if (school.tertiary_color) {
      document.documentElement.style.setProperty('--tertiary-color', school.tertiary_color);
    }
    if (school.button_color) {
      document.documentElement.style.setProperty('--button-color', school.button_color);
    }
    if (school.font_family) {
      document.documentElement.style.setProperty('--font-family', school.font_family);
      document.body.style.fontFamily = school.font_family;
    }
  }, [school.primary_color, school.secondary_color, school.tertiary_color, school.button_color, school.font_family]);

  /**
   * Update local school state immediately and trigger reload
   */
  const updateSchoolState = (updatedData) => {
    setSchool((prev) => ({ ...prev, ...updatedData }));
  };

  // Check if school license is expired or suspended for non-super_admin users
  const isLicenseExpired = Boolean(
    token &&
    user?.role !== 'super_admin' && (
      school.is_licensed === 0 ||
      school.is_licensed === false ||
      (school.license_expiry && new Date(school.license_expiry).getTime() < new Date().setHours(0, 0, 0, 0))
    )
  );

  return (
    <SchoolContext.Provider
      value={{
        school,
        loading,
        isLicenseExpired,
        reloadSchoolSettings: loadSchoolSettings,
        updateSchoolState
      }}
    >
      {children}

      {/* License Expired / Suspended Lockout Overlay for Tenant Users */}
      {isLicenseExpired && (
        <div className="fixed inset-0 z-[99999] bg-slate-950/90 backdrop-blur-xl flex items-center justify-center p-4">
          <div className="bg-gradient-to-b from-slate-900 to-indigo-950 border border-indigo-500/30 rounded-3xl p-8 max-w-lg w-full text-center text-white shadow-2xl space-y-6 animate-fadeIn">
            <div className="w-16 h-16 rounded-3xl bg-rose-500/20 border border-rose-500/30 text-rose-400 flex items-center justify-center mx-auto text-3xl shadow-inner">
              🔒
            </div>

            <div className="space-y-2">
              <span className="px-3 py-1 bg-rose-500/20 text-rose-300 text-[11px] font-mono font-black uppercase tracking-wider rounded-full border border-rose-500/30 inline-block">
                Subscription License Expired
              </span>
              <h2 className="text-2xl font-black text-white tracking-tight">
                {school.name || 'Tenant School Portal'}
              </h2>
              <p className="text-xs text-slate-300 max-w-sm mx-auto leading-relaxed">
                Access to this school portal has been suspended due to an expired subscription license. Please contact Super Admin Support to renew your license key.
              </p>
            </div>

            {/* Contact Support Box */}
            <div className="bg-slate-800/80 border border-slate-700/80 rounded-2xl p-4 text-left space-y-3">
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider font-mono">
                📞 Direct Support & License Renewal
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <a
                  href="tel:03171818108"
                  className="flex items-center justify-center gap-2 py-3 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs rounded-xl shadow-md transition"
                >
                  <span className="text-base">📞</span>
                  <span>Call: 0317-1818108</span>
                </a>

                <a
                  href={`https://wa.me/923171818108?text=${encodeURIComponent(`Hello Super Admin, I need to renew the license for my school "${school.name}" (ID: #${school.id}).`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 py-3 px-4 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs rounded-xl shadow-md transition"
                >
                  <span className="text-base">💬</span>
                  <span>WhatsApp Support</span>
                </a>
              </div>
            </div>

            <div className="pt-2">
              <button
                onClick={() => {
                  auth?.logout?.();
                  window.location.href = '/login';
                }}
                className="text-xs font-semibold text-slate-400 hover:text-white underline transition"
              >
                ← Logout & Switch Account
              </button>
            </div>
          </div>
        </div>
      )}
    </SchoolContext.Provider>
  );
};

export const useSchool = () => useContext(SchoolContext);
